<template>
  <router-link
    :to="to"
    class="group bg-white border border-slate-100 rounded-2xl shadow-sm p-8 hover:shadow-xl hover:border-slate-200 hover:-translate-y-0.5 transition-all duration-200 flex flex-col min-h-[230px]"
  >
    <!-- Top row: icon + arrow -->
    <div class="flex items-start justify-between mb-6">
      <div :class="['w-14 h-14 rounded-2xl flex items-center justify-center', iconBg]">
        <!-- Car -->
        <svg v-if="icon === 'car'" class="w-7 h-7" :class="iconColor" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM19 17H5v-5h14v5z"/>
          <circle cx="7.5" cy="14.5" r="1.5"/>
          <circle cx="16.5" cy="14.5" r="1.5"/>
        </svg>
        <!-- Map pin -->
        <svg v-else-if="icon === 'pin'" class="w-7 h-7" :class="iconColor" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
        <!-- Bell -->
        <svg v-else-if="icon === 'bell'" class="w-7 h-7" :class="iconColor" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
        </svg>
        <!-- Credit card -->
        <svg v-else-if="icon === 'card'" class="w-7 h-7" :class="iconColor" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
        </svg>
      </div>

      <svg class="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all mt-1.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
      </svg>
    </div>

    <!-- Stat count -->
    <div v-if="stat !== null" class="mb-1">
      <span class="text-4xl font-extrabold text-slate-800">{{ stat }}</span>
    </div>

    <!-- Title + description -->
    <div class="flex-1">
      <h3 :class="[
        'font-bold mb-2 transition-colors',
        stat !== null
          ? 'text-xs uppercase tracking-widest text-slate-400'
          : 'text-lg text-slate-800 group-hover:text-blue-700'
      ]">
        {{ title }}
      </h3>
      <p v-if="stat === null" class="text-sm text-slate-500 leading-relaxed">{{ description }}</p>
    </div>

    <!-- Open CTA -->
    <div :class="['mt-6 flex items-center gap-1.5 text-sm font-bold', actionColor]">
      Open
      <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
      </svg>
    </div>
  </router-link>
</template>

<script>
const colorMap = {
  blue:    { bg: 'bg-blue-50',    icon: 'text-blue-600',    action: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', action: 'text-emerald-600' },
  violet:  { bg: 'bg-violet-50',  icon: 'text-violet-600',  action: 'text-violet-600' },
  sky:     { bg: 'bg-sky-50',     icon: 'text-sky-600',     action: 'text-sky-600' },
}

export default {
  name: "DashboardCard",
  props: {
    title:       { type: String,  required: true },
    description: { type: String,  required: true },
    to:          { type: String,  required: true },
    icon:        { type: String,  default: 'car' },
    color:       { type: String,  default: 'blue' },
    stat:        { type: Number,  default: null },
  },
  computed: {
    iconBg()      { return (colorMap[this.color] || colorMap.blue).bg },
    iconColor()   { return (colorMap[this.color] || colorMap.blue).icon },
    actionColor() { return (colorMap[this.color] || colorMap.blue).action },
  },
};
</script>
